import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, NavLink, Link } from 'react-router-dom';
import {
  LayoutDashboard, Plug, Settings, Ticket,
  Bell, ChevronDown, Users, Phone, BarChart3, Brain, UserCircle2,
  Building2,
  GitBranch, Shield, ShieldCheck, Calendar, BookOpen, MessageSquare,
  TrendingUp, ClipboardList, ChevronLeft, ChevronRight, WandSparkles,
  Sun, Moon, Search, Menu, X, CircleHelp, ExternalLink, FileCode2, FolderPlus, Globe,
  Star } from 'lucide-react';
import { cn, Dialog, DialogContent, DialogTitle } from '@hts/ui';
import { useTheme } from './hooks/useTheme.js';
import { useGet } from './hooks/useApi.js';
import { useMenu } from './hooks/useMenu.js';
import { useNotificationPrefs, loadSeenIds, saveSeenIds, type NotificationCategory } from './lib/notificationPrefs.js';
import { relativeTime } from './lib/format.js';
import { initTheme } from './lib/theme.js';
import { LoginPage } from './pages/auth/LoginPage.js';
import { useAuth } from './hooks/useAuth.js';
import { AccessDenied } from './components/AccessDenied.js';
import { AccountMenu } from './components/AccountMenu.js';
import { ProfilePage } from './pages/ProfilePage.js';
import logoSquareUrl from './assets/logo-square.jpg';

// Pages
import { DashboardPage } from './pages/DashboardPage.js';
import { IntegrationsPage } from './pages/admin/IntegrationsPage.js';
import { TicketsPage } from './pages/work/TicketsPage.js';
import { TicketDetailPage } from './pages/work/TicketDetailPage.js';
import { DispatchBoardPage } from './pages/work/DispatchBoardPage.js';
import { SchedulePage } from './pages/work/SchedulePage.js';
import { CallsPage } from './pages/intelligence/CallsPage.js';
import { CallsPopPage } from './pages/intelligence/CallsPopPage.js';
import { CsatPage } from './pages/intelligence/CsatPage.js';
import { CsatSettingsPage } from './pages/intelligence/CsatSettingsPage.js';
import { CallApprovalsPage } from './pages/intelligence/CallApprovalsPage.js';
import { TicketInsightsPage } from './pages/intelligence/TicketInsightsPage.js';
import { AskAiPage } from './pages/intelligence/AskAiPage.js';
import { AnalyticsPage } from './pages/insights/AnalyticsPage.js';
import { CoachingPage } from './pages/insights/CoachingPage.js';
import { TechniciansPage } from './pages/people/TechniciansPage.js';
import { OnCallPage } from './pages/people/OnCallPage.js';
import { CockpitPage } from './pages/work/CockpitPage.js';
import { BoardPoliciesPage } from './pages/admin/BoardPoliciesPage.js';
import { UsersPage } from './pages/admin/UsersPage.js';
import { AuditLogPage } from './pages/admin/AuditLogPage.js';
import { SettingsPage } from './pages/admin/SettingsPage.js';
import { UserSettingsPage } from './pages/settings/UserSettingsPage.js';
import { AdminLunchPage } from './pages/admin/AdminLunchPage.js';
import { AiTrainingPage } from './pages/admin/AiTrainingPage.js';
import { ScriptLibraryPage } from './pages/admin/ScriptLibraryPage.js';
import { AppointmentTypesPage } from './pages/admin/AppointmentTypesPage.js';
import { CategorySuggestionsPage } from './pages/admin/CategorySuggestionsPage.js';
import { WorkspacesPage } from './pages/admin/WorkspacesPage.js';
import { AdminPage } from './pages/admin/AdminPage.js';
import { PublicBookingPage } from './pages/PublicBookingPage.js';

// Initialize theme
initTheme();

declare const __APP_VERSION__: string;

// ─── Navigation structure ─────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    ],
  },
  {
    id: 'work',
    label: 'Work',
    items: [
      { id: 'tickets', label: 'Tickets', icon: Ticket, path: '/tickets' },
      { id: 'cockpit', label: 'Cockpit', icon: LayoutDashboard, path: '/cockpit' },
      { id: 'dispatch', label: 'Dispatching', icon: GitBranch, path: '/dispatch' },
      { id: 'schedule', label: 'Scheduling', icon: Calendar, path: '/schedule' },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    items: [
      { id: 'calls', label: 'Calls', icon: Phone, path: '/calls' },
      { id: 'call-approvals', label: 'Call Approvals', icon: ShieldCheck, path: '/calls/approvals' },
      { id: 'ticket-insights', label: 'Ticket Insights', icon: Brain, path: '/intelligence/tickets' },
      { id: 'ask-ai', label: 'Ask AI', icon: MessageSquare, path: '/ask-ai' },
      { id: 'csat', label: 'CSAT', icon: Star, path: '/csat' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/analytics' },
      { id: 'coaching', label: 'Tech Coaching', icon: TrendingUp, path: '/coaching' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    items: [
      { id: 'technicians', label: 'Technicians & Skills', icon: Users, path: '/people/technicians' },
      { id: 'oncall', label: 'On-Call Roster', icon: Shield, path: '/people/oncall' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { id: 'user-settings', label: 'Settings', icon: UserCircle2, path: '/settings' },
      { id: 'admin', label: 'Admin', icon: Settings, path: '/admin/integrations' },
    ],
  },
] as const;

interface ShellNotification {
  id: string;
  category: NotificationCategory;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  detail: string;
  at: string;
  href: string;
}

/** Paths that render a page (breadcrumb parents outside this set are plain text). */
const ROUTABLE_PATHS = new Set<string>([
  ...NAV_GROUPS.flatMap(g => g.items.map(i => i.path as string)),
  '/tickets',
]);

// ─── Navigation search ────────────────────────────────────────────────────────
function NavigationSearch({ open, onClose, navigate, isAdmin, isOwner }: { open: boolean; onClose: () => void; navigate: (p: string) => void; isAdmin: boolean; isOwner: boolean }) {
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [selected, setSelected] = React.useState(0);

  const allItems = React.useMemo(() => NAV_GROUPS
    .filter(g => g.id !== 'admin' || isAdmin)
    .map(g => ({
      ...g,
      items: g.items,
    }))
    .flatMap(g =>
    g.items.map(i => ({ ...i, group: g.label }))
  ), [isAdmin]);

  const filtered = React.useMemo(() =>
    query.trim()
      ? allItems.filter(i => i.label.toLowerCase().includes(query.toLowerCase()))
      : allItems,
  [query, allItems]);

  React.useEffect(() => {
    if (open) { setQuery(''); setSelected(0); }
  }, [open]);

  const IconComp = ({ icon: Icon }: { icon: React.ElementType }) => <Icon className="h-4 w-4" />;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-xl top-[15vh] translate-y-0 overflow-hidden p-0"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Navigation search</DialogTitle>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[hsl(var(--border))]">
          <Search className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(0); }}
            placeholder="Search pages and actions…"
            className="flex-1 bg-transparent text-sm text-[hsl(var(--content))] placeholder:text-[hsl(var(--content-placeholder))] outline-none"
            aria-label="Command search"
            aria-autocomplete="list"
          />
          <kbd className="text-xs font-mono text-[hsl(var(--content-subtle))] border border-[hsl(var(--border))] rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto py-2" role="listbox" aria-label="Pages and actions">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-[hsl(var(--content-subtle))]">No results for &quot;{query}&quot;</div>
          ) : (
            filtered.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                onClick={() => { navigate(item.path); onClose(); }}
                role="option"
                aria-selected={selected === idx}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                  selected === idx
                    ? 'bg-brand-600 text-white'
                    : 'text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))]'
                )}
              >
                <span className={cn('shrink-0', selected === idx ? 'text-white' : 'text-[hsl(var(--content-subtle))]')}>
                  <IconComp icon={item.icon} />
                </span>
                <span className="font-medium flex-1 text-left">{item.label}</span>
                <span className={cn('text-xs', selected === idx ? 'text-blue-100' : 'text-[hsl(var(--content-subtle))]')}>{item.group}</span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── App Shell / Layout ───────────────────────────────────────────────────────
function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolved, toggle } = useTheme();
  const auth = useAuth();
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [cmdOpen, setCmdOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [workspaceError, setWorkspaceError] = React.useState('');
  const [switchingWorkspace, setSwitchingWorkspace] = React.useState(false);

  const helpMenu = useMenu(helpOpen, React.useCallback(() => setHelpOpen(false), []));

  // Live notification feed (derived server-side from tickets, dispatch, call
  // alerts and integration health). Refreshed every minute.
  const notif = useGet<{ items?: ShellNotification[] }>('/api/v1/notifications');
  const refetchNotifs = notif.refetch;
  React.useEffect(() => {
    const t = setInterval(() => refetchNotifs(), 60_000);
    return () => clearInterval(t);
  }, [refetchNotifs]);
  const [notifPrefs] = useNotificationPrefs();
  const [seenIds, setSeenIds] = React.useState<Set<string>>(loadSeenIds);
  const notifications = React.useMemo(
    () => (Array.isArray(notif.data?.items) ? notif.data!.items! : []).filter(n => notifPrefs[n.category] !== false),
    [notif.data, notifPrefs],
  );
  const unreadCount = notifications.filter(n => !seenIds.has(n.id)).length;
  function toggleNotifications() {
    setNotifOpen(v => {
      if (!v && notifications.length > 0) {
        const next = new Set([...seenIds, ...notifications.map(n => n.id)]);
        setSeenIds(next);
        saveSeenIds(next);
      }
      return !v;
    });
  }
  const activeRole = auth.status === 'authenticated'
    ? auth.user.memberships.find(m => m.tenantId === auth.user.activeTenantId)?.role
    : undefined;
  const isAdmin = activeRole === 'admin' || activeRole === 'owner';
  const isOwner = activeRole === 'owner';
  const activeWorkspace = auth.status === 'authenticated' ? auth.user.activeTenant : null;
  const memberships = auth.status === 'authenticated' ? auth.user.memberships : [];
  async function selectWorkspace(tenantId: string) {
    if (!tenantId || tenantId === (auth.status === 'authenticated' ? auth.user.activeTenantId : null)) return;
    setWorkspaceError('');
    setSwitchingWorkspace(true);
    try { await auth.switchTenant(tenantId); }
    catch (error) {
      setWorkspaceError(error instanceof Error ? error.message : 'Could not switch workspace');
      setSwitchingWorkspace(false);
    }
  }
  const visibleGroups = React.useMemo(() => NAV_GROUPS
    .filter(group => group.id !== 'admin' || isAdmin)
    .map(group => ({
      ...group,
      items: group.items,
    })), [isAdmin, isOwner]);
  const notifMenu = useMenu(notifOpen, React.useCallback(() => setNotifOpen(false), []));

  // On /tickets/:id the last crumb should read the ticket number (#14832), not the internal id.
  const ticketMatch = /^[/]tickets[/]([^/]+)$/.exec(location.pathname);
  const { data: crumbTicket } = useGet<{ cwId?: number }>(
    ticketMatch ? '/api/v1/tickets/' + ticketMatch[1] : null,
  );

  const breadcrumbs = React.useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    return parts.map((part, i) => {
      const isTicketId = Boolean(ticketMatch) && i === 1;
      const label = isTicketId
        ? (crumbTicket?.cwId ? '#' + crumbTicket.cwId : '...')
        : part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' ');
      const path = '/' + parts.slice(0, i + 1).join('/');
      return {
        label,
        path,
        current: i === parts.length - 1,
        // Group segments like /people or /admin have no page of their own.
        routable: ROUTABLE_PATHS.has(path),
      };
    });
  }, [location.pathname, ticketMatch, crumbTicket]);

  const sidebarEl = (
    <aside
      className={cn(
        'flex flex-col h-full bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--sidebar-border))] transition-all duration-200',
        collapsed ? 'w-16' : 'w-60'
      )}
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div className={cn('flex items-center h-16 px-3 border-b border-[hsl(var(--sidebar-border))] shrink-0', collapsed ? 'justify-center' : 'gap-3')}>
        <div className="shrink-0 flex h-9 w-9 items-center justify-center rounded-md bg-[#16171A] select-none overflow-hidden">
          <img src={logoSquareUrl} alt="TechSys Connect" className="h-full w-full object-cover" />
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate leading-tight">TechSys Connect</p>
            <p className="text-[10px] text-[hsl(var(--sidebar-content))] truncate">Operations console</p>
          </div>
        )}
        {!collapsed && (
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-[hsl(var(--sidebar-content))] hover:text-white transition-colors p-1"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-4 scrollbar-thin">
        {visibleGroups.map(group => (
          <div key={group.id}>
            {!collapsed && (
              <p className="px-2 mb-1 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--sidebar-content))] select-none">
                {group.label}
              </p>
            )}
            <ul className="space-y-0.5">
              {group.items.map(item => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/dashboard'}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) => cn(
                        'w-full flex items-center rounded-lg px-2 py-2 text-sm font-medium transition-all duration-100 group relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset',
                        collapsed ? 'justify-center' : 'gap-2.5',
                        isActive
                          ? 'nav-item-active text-white'
                          : 'text-[hsl(var(--sidebar-content))] hover:bg-[hsl(var(--sidebar-item-hover))] hover:text-white'
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {!collapsed && (
                        <span className="flex-1 truncate text-left">{item.label}</span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse toggle (desktop) */}
      <div className="hidden lg:flex items-center justify-between p-2 border-t border-[hsl(var(--sidebar-border))]">
        <button
          onClick={() => setCollapsed(v => !v)}
          className="flex h-7 w-7 items-center justify-center rounded-md text-[hsl(var(--sidebar-content))] hover:bg-[hsl(var(--sidebar-item-hover))] hover:text-white transition-colors ml-auto"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>
      </div>
    </aside>
  );

  return (
    <div className={cn('flex h-screen overflow-hidden bg-[hsl(var(--surface-subtle))]', resolved)}>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      {/* Mobile sidebar */}
      <div className={cn(
        'fixed inset-y-0 left-0 z-50 lg:hidden transition-transform duration-200',
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {sidebarEl}
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-col lg:shrink-0">
        {sidebarEl}
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center gap-3 h-14 px-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] shrink-0">
          <button
            className="lg:hidden flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="h-4 w-4" />
          </button>

          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="hidden sm:flex min-w-0 flex-1">
            <ol className="flex items-center gap-1 text-sm min-w-0 m-0 p-0 list-none">
              <li className="flex items-center gap-1">
                <Link
                  to="/dashboard"
                  className="text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]"
                >
                  Home
                </Link>
              </li>
              {breadcrumbs.map((crumb) => (
                <li key={crumb.path} className="flex items-center gap-1 min-w-0">
                  <span className="text-[hsl(var(--content-subtle))] select-none" aria-hidden="true">›</span>
                  {crumb.current || !crumb.routable ? (
                    <span
                      aria-current={crumb.current ? 'page' : undefined}
                      className={cn(
                        'truncate max-w-[200px]',
                        crumb.current ? 'font-medium text-[hsl(var(--content))]' : 'text-[hsl(var(--content-subtle))]',
                      )}
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(crumb.path)}
                      className="truncate max-w-[200px] text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]"
                    >
                      {crumb.label}
                    </button>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          {/* Navigation search button */}
          <button
            onClick={() => setCmdOpen(true)}
            className="hidden md:flex items-center gap-2 h-8 px-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] text-[hsl(var(--content-subtle))] text-sm hover:border-[hsl(var(--border-strong))] hover:bg-[hsl(var(--surface))] transition-colors"
            aria-label="Search pages"
          >
            <Search className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="hidden lg:block">Search…</span>
          </button>

          {/* Actions */}
          <div className="ml-auto flex items-center gap-1">
            {auth.status === 'authenticated' && (
              <div className="relative mr-1">
                <label className="sr-only" htmlFor="workspace-selector">Active workspace</label>
                <select
                  id="workspace-selector"
                  aria-label="Active workspace"
                  value={auth.user.activeTenantId ?? ''}
                  disabled={switchingWorkspace}
                  onChange={(event) => void selectWorkspace(event.target.value)}
                  className="h-8 max-w-[9rem] sm:max-w-[14rem] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] px-2 text-xs font-medium text-[hsl(var(--content))] disabled:opacity-60"
                  title={activeWorkspace ? 'Active workspace: ' + activeWorkspace.name : 'Choose a workspace'}
                >
                  {!auth.user.activeTenantId && <option value="">Choose workspace…</option>}
                  {memberships.map(m => <option key={m.tenantId} value={m.tenantId}>{m.tenant?.name ?? m.tenantId}</option>)}
                </select>
                {workspaceError && <p role="alert" className="absolute right-0 top-9 z-30 w-64 rounded-lg border border-red-300 bg-[hsl(var(--surface))] p-2 text-xs text-red-600 shadow-lg">{workspaceError}</p>}
              </div>
            )}
            <div className="relative">
              <button
                ref={helpMenu.triggerRef}
                onClick={() => setHelpOpen(v => !v)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                aria-label="Help and documentation"
                aria-haspopup="menu"
                aria-expanded={helpOpen}
              >
                <CircleHelp className="h-4 w-4" aria-hidden="true" />
              </button>
              {helpOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setHelpOpen(false)} aria-hidden="true" />
                  <div
                    ref={helpMenu.menuRef}
                    role="menu"
                    aria-label="Help"
                    className="absolute right-0 top-10 z-20 w-64 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-xl overflow-hidden"
                  >
                    <a
                      role="menuitem"
                      href="/USER_GUIDE.pdf"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors border-b border-[hsl(var(--border))]"
                    >
                      <BookOpen className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" aria-hidden="true" />
                      <span className="flex-1">User Guide</span>
                      <ExternalLink className="h-3.5 w-3.5 text-[hsl(var(--content-placeholder))] shrink-0" aria-hidden="true" />
                    </a>
                    <div className="px-4 py-2 text-xs text-[hsl(var(--content-subtle))] border-b border-[hsl(var(--border))]" role="presentation">
                      TechSys Connect · v{__APP_VERSION__}
                    </div>
                    <a
                      role="menuitem"
                      href="https://github.com/houstontechsys/techsysconnect/tree/main/docs"
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                    >
                      <GitBranch className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" aria-hidden="true" />
                      <span className="flex-1">Docs on GitHub</span>
                      <ExternalLink className="h-3.5 w-3.5 text-[hsl(var(--content-placeholder))] shrink-0" aria-hidden="true" />
                    </a>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={toggle}
              className="flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
              aria-label={`Switch to ${resolved === 'dark' ? 'light' : 'dark'} mode`}
            >
              {resolved === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <div className="relative">
              <button
                ref={notifMenu.triggerRef}
                onClick={toggleNotifications}
                className="relative flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                aria-label={unreadCount > 0 ? `Notifications (${unreadCount} new)` : 'Notifications'}
                aria-haspopup="menu"
                aria-expanded={notifOpen}
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 border-2 border-[hsl(var(--surface))]" aria-hidden="true" />
                )}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setNotifOpen(false)} aria-hidden="true" />
                  <div
                    ref={notifMenu.menuRef}
                    role="menu"
                    aria-label="Notifications"
                    className="absolute right-0 top-10 z-20 w-80 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-xl overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-[hsl(var(--border))] flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Notifications</h3>
                      <button
                        type="button"
                        onClick={() => { setNotifOpen(false); navigate('/admin/settings'); }}
                        className="text-xs text-brand-600 dark:text-brand-400 hover:underline"
                      >
                        Preferences
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notif.loading && !notif.data ? (
                        <p className="px-4 py-6 text-center text-xs text-[hsl(var(--content-subtle))]" role="status">Loading…</p>
                      ) : notif.error ? (
                        <div className="px-4 py-6 text-center text-xs text-[hsl(var(--content-subtle))]">
                          Couldn&apos;t load notifications ({notif.error}).{' '}
                          <button type="button" onClick={() => notif.refetch()} className="text-brand-600 dark:text-brand-400 hover:underline">Retry</button>
                        </div>
                      ) : notifications.length === 0 ? (
                        <p className="px-4 py-6 text-center text-xs text-[hsl(var(--content-subtle))]">You&apos;re all caught up.</p>
                      ) : (
                        notifications.map(n => (
                          <button
                            key={n.id}
                            type="button"
                            role="menuitem"
                            onClick={() => { setNotifOpen(false); navigate(n.href); }}
                            className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-[hsl(var(--surface-subtle))] border-b border-[hsl(var(--border))] last:border-0"
                          >
                            <span
                              className={cn(
                                'mt-1.5 h-2 w-2 rounded-full shrink-0',
                                n.severity === 'critical' ? 'bg-red-500' : n.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500',
                              )}
                              aria-hidden="true"
                            />
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-medium text-[hsl(var(--content))] leading-tight">{n.title}</span>
                              <span className="block text-xs text-[hsl(var(--content-subtle))] truncate">{n.detail}</span>
                            </span>
                            <span className="text-xs text-[hsl(var(--content-placeholder))] shrink-0">{relativeTime(n.at)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {auth.status === 'authenticated' && (
              <AccountMenu
                user={auth.user}
                activeRole={activeRole}
                theme={resolved}
                onToggleTheme={toggle}
                onSignOut={() => { void auth.signOut().then(() => navigate('/login')); }}
              />
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto" id="main-content">
          {auth.status === 'authenticated' && !auth.user.activeTenantId && auth.user.memberships.length > 1 ? (
            <div className="flex min-h-full items-center justify-center p-6">
              <div className="max-w-md rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-6 text-center shadow-sm">
                <Globe className="mx-auto h-8 w-8 text-brand-600" />
                <h1 className="mt-3 text-lg font-semibold text-[hsl(var(--content))]">Choose your workspace</h1>
                <p className="mt-1 text-sm text-[hsl(var(--content-subtle))]">Select the organization whose data you want to view. Your current page will be preserved.</p>
              </div>
            </div>
          ) : (
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/cockpit" element={<CockpitPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/tickets" element={<TicketsPage />} />
            <Route path="/tickets/:id" element={<TicketDetailPage />} />
            <Route path="/dispatch" element={<DispatchBoardPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/dispatching" element={<Navigate to="/dispatch" replace />} />
            <Route path="/scheduling" element={<Navigate to="/schedule" replace />} />
            <Route path="/calls" element={<CallsPage />} />
            {/* 3CX screen-pop target (issue #61): opened by the 3CX Windows client with ?number=%CallerNumber% */}
            <Route path="/calls/pop" element={<CallsPopPage />} />
            <Route path="/csat" element={<CsatPage />} />
            <Route path="/csat/settings" element={<CsatSettingsPage />} />
            <Route path="/calls/approvals" element={<CallApprovalsPage />} />
            <Route path="/intelligence/tickets" element={<TicketInsightsPage />} />
            <Route path="/ask-ai" element={<AskAiPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/coaching" element={<CoachingPage />} />
            <Route path="/people/technicians" element={<TechniciansPage />} />
            <Route path="/people/oncall" element={<OnCallPage />} />
            <Route path="/admin/integrations" element={isAdmin ? <AdminPage><IntegrationsPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/boards" element={isAdmin ? <AdminPage><BoardPoliciesPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/appointment-types" element={isAdmin ? <AdminPage><AppointmentTypesPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/users" element={isAdmin ? <AdminPage><UsersPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/workspaces" element={isAdmin ? <AdminPage><WorkspacesPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/audit" element={isAdmin ? <AdminPage><AuditLogPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/ai-training" element={isAdmin ? <AdminPage><AiTrainingPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/script-library" element={isAdmin ? <AdminPage><ScriptLibraryPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/category-suggestions" element={isAdmin ? <AdminPage><CategorySuggestionsPage /></AdminPage> : <AccessDenied />} />
            <Route path="/admin/settings" element={isAdmin ? <AdminPage><SettingsPage /></AdminPage> : <AccessDenied />} />
            <Route path="/settings" element={<UserSettingsPage />} />
            <Route path="/admin/lunch" element={isAdmin ? <AdminPage><AdminLunchPage /></AdminPage> : <AccessDenied />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          )}
        </main>
      </div>

      {/* Overlays */}
      <NavigationSearch open={cmdOpen} onClose={() => setCmdOpen(false)} navigate={navigate} isAdmin={isAdmin} isOwner={isOwner} />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/book/:token" element={<PublicBookingPage />} />
      <Route path="/*" element={<RequireAuth><AppLayout /></RequireAuth>} />
    </Routes>
  );
}

/** Gate: waits for the session check, then renders children or redirects to /login. */
function RequireAuth({ children }: { children: React.ReactElement }) {
  const auth = useAuth();
  if (auth.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--surface))]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-brand-600 text-white font-bold text-sm flex items-center justify-center shadow-md">TS</div>
          <div className="h-1 w-32 rounded-full bg-[hsl(var(--surface-subtle))] overflow-hidden">
            <div className="h-full w-1/2 bg-brand-600 animate-pulse" />
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))]" role="status">Checking your session…</p>
        </div>
      </div>
    );
  }
  if (auth.status === 'signedout') {
    return <Navigate to="/login" replace />;
  }
  return children;
}
